
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import { i18n } from "@/i18n";
import {
  IServicepointUserTable,
  ISolResponse,
  IUpdateUser,
  IUpdateUserClient,
  IUser,
  IUserDetail,
} from "@/entities";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import Pagination from "@/components/common/Pagination.vue";
import { Action, Getter } from "vuex-class";
import FilterUserServicepoints from "@/components/user/FilterUserServicepoints.vue";
import { EventBus } from "@/event-bus";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import { SweetAlertResult } from "sweetalert2";
import UserServicepointsTableAction from "./UserServicepointsTableAction.vue";

@Component({
  name: "user-servicepoints-table",
  components: {
    FilterUserServicepoints,
    UserServicepointsTableAction,
    IconTrash,
    Pagination,
    IconEdit,
  },
})
export default class UserServicepointsTable extends Vue {
  @Action("removeUserServicepoint") removeUserServicepoint!: ({
    idUser,
    updateUser,
  }: {
    idUser?: number;
    updateUser: IUpdateUser;
  }) => Promise<ISolResponse | undefined>;
  @Prop({ required: true }) user!: IUserDetail;
  @Getter("getLoggedUser") loggedUser: IUser | undefined;

  public sortBy = "client";
  public sortDesc = false;
  public pageSize = 10;
  public currentPage = 1;
  public params: IServicepointUserTable = {};
  public checkedAll = false;
  public allDeselect = true;
  public component = "servicepoints";

  get selectAllLenght(): number {
    return this.servicepointsFiltered.filter((servicepoint) => servicepoint.checked).length;
  }

  get isNotSameUserAndIsAdmin(): boolean {
    const userExists = Boolean(this.user && this.user.username);
    const isDifferentUser = userExists && this.user.username !== this.loggedUser?.username;
    const isAdmin = Boolean(this.loggedUser?.admin);
    return isDifferentUser || isAdmin;
  }

  checkDeleteDisabled(): void {
    // Usar doble $nextTick para asegurar que todas las actualizaciones de Vue se completen
    this.$nextTick(() => {
      this.$nextTick(() => {
        const selectedCount = this.servicepointsFiltered.filter(
          (servicepoint) => servicepoint.checked === true
        ).length;
        this.allDeselect = selectedCount === 0;
      });
    });
  }

  selectOne(): void {
    this.checkedAll = this.servicepointsFiltered.every((servicepoint) => servicepoint.checked);
    this.checkDeleteDisabled();
  }

  selectAll(): void {
    if (!this.checkedAll) {
      this.servicepointsFiltered.forEach((servicepoint) => (servicepoint.checked = true));
    } else {
      this.servicepointsFiltered.forEach((servicepoint) => (servicepoint.checked = false));
    }
    this.checkedAll = !this.checkedAll;
    this.checkDeleteDisabled();
  }

  constructor() {
    super();
  }

  mounted(): void {
    EventBus.$on("filter-user-servicepoints-table", () => {
      this.onChangeCurrentPage(1);
    });
    this.checkDeleteDisabled();
  }

  async beforeDestroy(): Promise<void> {
    EventBus.$off("filter-user-servicepoints-table");
  }

  get userApprovalsChangeDetector(): string {
    return JSON.stringify(this.user.approvals);
  }

  @Watch("userApprovalsChangeDetector")
  onUserApprovalsChange(): void {
    this.checkedAll = false;
    this.$nextTick(() => {
      this.checkDeleteDisabled();
    });
  }

  get servicepoints(): IServicepointUserTable[] {
    const servicepoints: IServicepointUserTable[] = [];

    this.user?.clients?.forEach((client) => {
      if (!client.subclients?.length) {
        servicepoints.push({
          clientId: client.id,
          client: `${client.codeSol} - ${client.description}`,
          checked: false,
        });
      } else {
        client.subclients?.forEach((center) => {
          center.servicepoints?.forEach((servicepoint) => {
            servicepoints.push({
              clientId: client.id,
              client: `${client.codeSol} - ${client.description}`,
              centerId: center.id,
              center: `${center.codeSol} - ${center.description}`,
              id: servicepoint.id,
              description: `${servicepoint.codeSol} - ${servicepoint.description}`,
              checked: false,
            });
          });
        });
      }
    });

    return servicepoints;
  }

  get servicepointsFiltered(): IServicepointUserTable[] {
    return this.servicepoints.filter((servicepoint) => {
      return (
        (!this.params.client ||
          servicepoint.client?.toUpperCase().includes(this.params.client.toUpperCase())) &&
        (!this.params.center ||
          servicepoint.center?.toUpperCase().includes(this.params.center.toUpperCase())) &&
        (!this.params.description ||
          servicepoint.description?.toUpperCase().includes(this.params.description.toUpperCase()))
      );
    });
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-first",
      },
      {
        key: "client",
        label: i18n.t("label.client") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "center",
        label: i18n.t("label.subclient") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "description",
        label: i18n.t("label.servicepoint") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  onChangeCurrentPage(currentPage: number): void {
    this.currentPage = currentPage;
    // Verificar estado del boton al cambiar página
    this.checkDeleteDisabled();
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.selectDelete.confirm.title", [this.selectAllLenght]).toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  async removeServicepoints(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      let updateUser: IUpdateUser;
      let clients: IUpdateUserClient[] = [];

      this.servicepointsFiltered.forEach((servicepoint) => {
        if (servicepoint.checked) {
          if (this.user.id && servicepoint.clientId && !servicepoint.id) {
            clients.push({
              id: servicepoint.clientId,
              subclients: [],
            });
          } else {
            clients.push({
              id: servicepoint.clientId!,
              subclients: [
                {
                  id: servicepoint.centerId,
                  servicepoints: [
                    {
                      id: servicepoint.id,
                    },
                  ],
                },
              ],
            });
          }
        }
      });
      updateUser = {
        addOnlyClient: false,
        numberOfServicesPoint: this.loggedUser?.servicesPointOnCashUser || 0,
        clients: clients,
      };
      const response = await this.removeUserServicepoint({ idUser: this.user.id, updateUser });
      this.$emit("change", this.user.id);
      if (response?.updatedSmartcash) {
        this.makeToast("success", i18n.t("validation.messages.user.ok").toString());
      } else {
        this.makeToast("warning", i18n.t("validation.messages.user.ko").toString());
      }
    }
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
